import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { map } from 'rxjs';
import { AuthService, CountriesData, CountriesService, CountryNamePipe, InviteRegistration, StorageService, UserRegistration, UserService, Util } from '@wm-libs/core';
import { AlertService } from '@wm-libs/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AfterSignupComponent } from '../after-signup/after-signup.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { NgIf, NgClass, NgFor, AsyncPipe, KeyValuePipe } from '@angular/common';

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['../../styles/auth.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgClass,
    MatCardModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    NgFor,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatDatepickerModule,
    MatCheckboxModule,
    RouterLink,
    AfterSignupComponent,
    AsyncPipe,
    TranslateModule,
    CountryNamePipe,
    KeyValuePipe,
  ],
})
export class SignUpComponent implements OnInit {
  signupForm!: FormGroup;
  hidePassword = true;
  hideConfirmPassword = true;
  isMobile$ = this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(map((result) => result.matches));

  profileValidation = false;
  userEmail!: string;
  afterSignupLogic = false;

  defaultCountryByLocation = '';

  countries!: CountriesData;
  invitationCode!: string;
  invitedEmail!: string;

  pageLoaded = false;

  constructor(
    private authService: AuthService,
    private fb: FormBuilder,
    private breakpointObserver: BreakpointObserver,
    private countriesService: CountriesService,
    private alertService: AlertService,
    private translateService: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private storageService: StorageService
  ) {
    this.countriesService.getCountries().subscribe((res) => {
      this.countries = res;
    });

    this.signupForm = this.fb.group(
      {
        country: ['', Validators.required],
        personalName: ['', Validators.required],
        personalSurname: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, Util.passwordValidator()]],
        confirmPassword: ['', Validators.required],
        agreeToTerms: [false, Validators.requiredTrue],
      },
      { validators: Util.passwordsMatch }
    );
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.invitationCode = params['invitationCode'];

      if (this.invitationCode) {
        this.checkInvitationCode();
      } else {
        this.pageLoaded = true;
      }
    });

    this.countriesService.getCountryByLocation().subscribe((res) => {
      this.defaultCountryByLocation = res;
    });
  }

  checkInvitationCode() {
    this.authService.checkInvitationCode(this.invitationCode).subscribe(
      (res) => {
        this.invitedEmail = res?.email;

        this.signupForm.patchValue({ email: this.invitedEmail });
        this.signupForm.get('email')?.disable();
        this.pageLoaded = true;
      },
      () => {
        this.pageLoaded = true;
        this.router.navigate(['/page-not-found']);
      }
    );
  }

  signUp() {
    if (this.invitedEmail) {
      this.signupAfterInvite();

      return;
    }

    if (this.signupForm.valid) {
      const userRegistration: UserRegistration = {
        email: this.signupForm?.get('email')?.value,
        password: this.signupForm?.get('password')?.value,
        country: this.signupForm?.get('country')?.value,
      };

      const firstName = this.signupForm?.get('personalName')?.value;
      const lastName = this.signupForm?.get('personalSurname')?.value;

      if (firstName) {
        userRegistration.firstName = firstName;
      }

      if (lastName) {
        userRegistration.lastName = lastName;
      }

      this.authService.signup(userRegistration).subscribe(() => {
        this.userEmail = userRegistration.email;
        this.afterSignupLogic = true;
      });
    } else {
      this.alertService.showAlert(this.translateService.instant('FORMS.NOT_VALID'), 'error');
    }
  }

  signupAfterInvite() {
    if (this.signupForm.valid) {
      const userRegistration: InviteRegistration = {
        password: this.signupForm?.get('password')?.value,
        country: this.signupForm?.get('country')?.value,
      };

      const firstName = this.signupForm?.get('personalName')?.value;
      const lastName = this.signupForm?.get('personalSurname')?.value;

      if (firstName) {
        userRegistration.firstName = firstName;
      }

      if (lastName) {
        userRegistration.lastName = lastName;
      }

      this.authService.signupAfterInvitate(this.invitationCode, userRegistration).subscribe(() => {
        this.logOut();

        this.router.navigate(['/auth/sign-in']);
        this.alertService.showAlert(this.translateService.instant('USER.ACCOUNT.ACTIVATED'), 'success');
      });
    } else {
      this.alertService.showAlert(this.translateService.instant('FORMS.NOT_VALID'), 'error');
    }
  }

  logOut() {
    this.authService.logout().subscribe(() => {
      this.storageService.removeToken();
      this.userService.setAuthenticated(false);
      this.userService.setNeedTfa(false);
    });
  }
}
